import axios from 'axios';
import PropTypes from 'prop-types';
import './Cart.css';
import { useState, useRef, useMemo } from 'react';
import ItemType from '../types/item';
import CartRow from './CartRow';
import Alert from './Alert';
import { CartTypes } from '../reducers/cartReducer';

function Cart({ cart, items, dispatch }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [isEmployeeOfTheMonth, setIsEmployeeOfTheMonth] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [apiError, setApiError] = useState('');
  const debounceRef = useRef(null);
  const zipRef = useRef(null);
  const nameRef = useRef(null);
  const isFormValid = name && zipcode.length === 5;
  const subTotal = isEmployeeOfTheMonth
    ? 0 : cart.reduce(
      (acc, item) => acc
      + (items.find((i) => i.itemId === item.itemId)?.salePrice
        ?? items.find((i) => i.itemId === item.itemId).price
      )
        * item.quantity,
      0,
    );
  const taxRate = useMemo(
    () => {
      console.log(zipcode);
      return Number(zipcode[0]) * 0.01 || 0.01;
    },
    [zipcode],
  );
  const tax = (subTotal * taxRate).toFixed(2);
  const total = (Number(subTotal) + Number(tax)).toFixed(2);
  const submitOrder = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setApiError('');
    try {
      await axios.post('/api/orders', {
        name,
        phone,
        zipCode: zipcode,
        items: cart,
      });
      console.log('Order submitted');
      dispatch({ type: CartTypes.CLEAR });
      setShowSuccessAlert(true);
    } catch (error) {
      console.error(error);
      setApiError(error?.response?.data?.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };
  const setFormattedPhone = (newNumber) => {
    const digits = newNumber.replace(/\D/g, '');
    setPhone(digits);
    if (digits.length === 11) {
      zipRef.current.focus();
    }
  };

  const onNameChange = (newName) => {
    setName(newName);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      axios
        .get(`/api/employees/isEmployeeOfTheMonth?name=${newName}`)
        .then((response) => {
          setIsEmployeeOfTheMonth(response?.data?.isEmployeeOfTheMonth);
        })
        .catch(console.error);
    }, 500);
  };
  const setZipcodeWithFocus = (newZipcode) => {
    setZipcode(newZipcode);
    if (newZipcode.length === 5 && !name) {
      nameRef.current.focus();
    }
  };
  return (
    <div className="cart-component">
      <Alert visible={showSuccessAlert} type="success">
        Thank you for your order!
      </Alert>
      <Alert visible={!!apiError} type="error">
        <p>There was an error processing your order:</p>
        <p>{apiError}</p>
        <p>Please try again.</p>
      </Alert>
      <h2>Your Cart</h2>
      {cart.length === 0 ? (
        <div>Cart is Empty</div>
      ) : (
        <>
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Quantity</th>
                <th>Price</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {cart.map((item) => (
                <CartRow
                  key={item.itemId}
                  cartItem={item}
                  items={items}
                  dispatch={dispatch}
                />
              ))}
            </tbody>
          </table>
          <div>
            Subtotal: $
            {subTotal.toFixed(2)}
          </div>
          {zipcode.length === 5 ? (
            <>
              <div>
                Tax: $
                {tax}
              </div>
              <div>
                Total: $
                {total}
              </div>
            </>
          ) : (
            <div className="warning">Please enter a valid zipcode</div>
          )}

          <h2>Checkout</h2>
          <form onSubmit={submitOrder}>
            <label htmlFor="name">
              Name:
              <input
                required
                ref={nameRef}
                type="text"
                id="name"
                name="name"
                style={{ width: 300 }}
                value={name}
                onChange={(event) => onNameChange(event.target.value)}
              />
            </label>
            <label htmlFor="phone">
              Phone:
              <input
                type="tel"
                value={phone}
                id="phone"
                name="phone"
                style={{ width: 300 }}
                onChange={(event) => setFormattedPhone(event.target.value)}
              />
            </label>
            <label htmlFor="zipcode">
              Zipcode:
              <input
                required
                ref={zipRef}
                type="text"
                id="zipcode"
                name="zipcode"
                style={{ width: 300 }}
                maxLength="5"
                inputMode="numeric"
                value={zipcode}
                onChange={(event) => setZipcodeWithFocus(event.target.value)}
              />
            </label>
            <button type="submit" disabled={!isFormValid || isSubmitting}>
              Order Now
            </button>
          </form>
        </>
      )}
    </div>
  );
}

Cart.propTypes = {
  cart: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string.isRequired,
    quantity: PropTypes.number.isRequired,
  })).isRequired,
  items: PropTypes.arrayOf(ItemType).isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default Cart;
